/*
 * Date-range derivation per Calendar view + label formatting.
 * DESIGN_SYSTEM.md §2.2.
 */

import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  format,
  isSameMonth,
  isSameYear,
  addDays,
  addWeeks,
  addMonths,
  isSameDay,
} from 'date-fns';
import type { Locale } from 'date-fns';
import type { CalendarView } from './types';

export interface DateRange {
  start: Date;
  end: Date;
}

export function rangeForView(view: CalendarView, anchor: Date, weekStartsOn: 0 | 1 = 1): DateRange {
  switch (view) {
    case 'day':
    case 'team':
      return { start: startOfDay(anchor), end: endOfDay(anchor) };
    case 'week':
      return {
        start: startOfWeek(anchor, { weekStartsOn }),
        end: endOfWeek(anchor, { weekStartsOn }),
      };
    case 'month':
      return { start: startOfMonth(anchor), end: endOfMonth(anchor) };
  }
}

export function shiftAnchor(view: CalendarView, anchor: Date, direction: 1 | -1): Date {
  switch (view) {
    case 'day':
    case 'team':
      return addDays(anchor, direction);
    case 'week':
      return addWeeks(anchor, direction);
    case 'month':
      return addMonths(anchor, direction);
  }
}

/** Header label like "Thursday 8 May 2026", "5 – 11 May 2026", "May 2026". */
export function labelForView(view: CalendarView, anchor: Date, locale?: Locale, opts?: { compact?: boolean }): string {
  const compact = opts?.compact ?? false;
  const fmtOpts = locale ? { locale } : undefined;
  switch (view) {
    case 'day':
    case 'team':
      return format(anchor, compact ? 'EEE d MMM yyyy' : 'EEEE d MMMM yyyy', fmtOpts);
    case 'week': {
      const range = rangeForView(view, anchor);
      const a = range.start;
      const b = range.end;
      if (isSameMonth(a, b)) {
        const monthFmt = compact ? 'd MMM yyyy' : 'd MMMM yyyy';
        return `${format(a, 'd', fmtOpts)} – ${format(b, monthFmt, fmtOpts)}`;
      }
      if (isSameYear(a, b)) {
        return `${format(a, 'd MMM', fmtOpts)} – ${format(b, 'd MMM yyyy', fmtOpts)}`;
      }
      return `${format(a, 'd MMM yyyy', fmtOpts)} – ${format(b, 'd MMM yyyy', fmtOpts)}`;
    }
    case 'month':
      return format(anchor, compact ? 'MMM yyyy' : 'MMMM yyyy', fmtOpts);
  }
}

/** Days-of-week for the week view, derived from anchor + weekStartsOn. */
export function daysInWeek(anchor: Date, weekStartsOn: 0 | 1 = 1): Date[] {
  const start = startOfWeek(anchor, { weekStartsOn });
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

/** Returns `true` when `d` falls today (local). */
export function isToday(d: Date): boolean {
  return isSameDay(d, new Date());
}
